import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Image as ImageIcon, Eye, EyeOff, Trash2, CheckCircle,
  Upload, Loader2, GripVertical, Pencil, X, Save,
} from "lucide-react";
import {
  DndContext, closestCenter, PointerSensor, TouchSensor, useSensor, useSensors,
} from "@dnd-kit/core";
import {
  arrayMove, SortableContext, useSortable, rectSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Input } from "../components/ui/input";
import { SectionCard, EmptyState, StatCard } from "./AdminUI";
import { compressImage } from "../utils/compressImage";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * One draggable gallery tile. The grip lives on top-left and only that handle
 * captures drag events — clicking anywhere else on the tile still fires the
 * toggle/delete buttons on mobile (which are always visible below md).
 */
const SortableGalleryTile = ({ img, onToggle, onDelete, onEdit }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: img.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
    zIndex: isDragging ? 50 : "auto",
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`gallery-tile-${img.id}`}
      className={`group relative rounded-xl overflow-hidden border border-white/[0.06] ${!img.is_active && "opacity-50"}`}
    >
      <img
        src={img.url}
        alt={img.caption || "Image galerie"}
        className="w-full aspect-square object-cover pointer-events-none select-none"
        onError={(e) => { e.target.src = "https://via.placeholder.com/300x300?text=Image+invalide"; }}
      />

      {/* Drag handle — top-left, always visible so mobile users see it too */}
      <button
        type="button"
        {...attributes}
        {...listeners}
        data-testid={`gallery-drag-${img.id}`}
        className="absolute top-2 left-2 p-1.5 bg-black/60 hover:bg-black/80 text-white/90 rounded-md cursor-grab active:cursor-grabbing touch-none"
        aria-label="Réordonner"
        title="Glisser pour réordonner"
      >
        <GripVertical size={14} />
      </button>

      {/* Actions — always visible on mobile, on hover on desktop */}
      <div className="absolute bottom-2 right-2 flex items-center gap-1.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
        <button
          type="button"
          onClick={() => onEdit(img)}
          data-testid={`gallery-edit-${img.id}`}
          className="p-2 bg-white/[0.14] hover:bg-white/[0.24] text-white rounded-md transition-colors"
          title="Modifier la légende"
        >
          <Pencil size={14} />
        </button>
        <button
          type="button"
          onClick={() => onToggle(img.id)}
          data-testid={`gallery-toggle-${img.id}`}
          className={`p-2 rounded-md ${img.is_active ? "bg-amber-500 hover:bg-amber-600" : "bg-emerald-500 hover:bg-emerald-600"} text-white transition-colors`}
          title={img.is_active ? "Désactiver" : "Activer"}
        >
          {img.is_active ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
        <button
          type="button"
          onClick={() => onDelete(img.id)}
          data-testid={`gallery-delete-${img.id}`}
          className="p-2 bg-red-500 hover:bg-red-600 text-white rounded-md transition-colors"
          title="Supprimer"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {img.caption && (
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/85 to-transparent p-3 pb-9 pointer-events-none">
          <p className="text-xs text-white truncate">{img.caption}</p>
        </div>
      )}
      <div className="absolute top-2 right-2 pointer-events-none">
        {img.is_active ? (
          <span className="bg-emerald-500 text-white text-[10px] font-medium px-2 py-0.5 rounded-md">Actif</span>
        ) : (
          <span className="bg-white/30 text-white text-[10px] font-medium px-2 py-0.5 rounded-md">Inactif</span>
        )}
      </div>
    </div>
  );
};

const GalleryTab = ({ ctx }) => {
  const {
    galleryImages, galleryForm, setGalleryForm,
    handleAddImage, handleToggleImage, handleDeleteImage,
  } = ctx;

  const activeCount = galleryImages.filter((i) => i.is_active).length;
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  // Local ordered copy so drag & drop feels instant. Kept in sync with parent state.
  const [orderedImages, setOrderedImages] = useState(galleryImages);
  const [reordering, setReordering] = useState(false);
  // Edit modal state: null = closed, else { id, caption }
  const [editingImage, setEditingImage] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  useEffect(() => { setOrderedImages(galleryImages); }, [galleryImages]);

  // Both PointerSensor (mouse/pen) and TouchSensor (mobile) so drag works everywhere.
  // Small activation distance/delay prevents accidental drag when tapping toggle/delete.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = orderedImages.findIndex((i) => i.id === active.id);
    const newIndex = orderedImages.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(orderedImages, oldIndex, newIndex);
    setOrderedImages(reordered);  // optimistic UI
    setReordering(true);
    try {
      await axios.post(`${API}/api/admin/gallery/reorder`, {
        order: reordered.map((i) => i.id),
      });
      toast.success("Ordre enregistré");
    } catch (err) {
      toast.error("Erreur au réordonnement");
      setOrderedImages(galleryImages);  // rollback
    } finally {
      setReordering(false);
    }
  };

  const handleEditClick = (img) => setEditingImage({ id: img.id, caption: img.caption || "" });

  const handleSaveCaption = async () => {
    if (!editingImage) return;
    setSavingEdit(true);
    try {
      await axios.put(`${API}/api/admin/gallery/${editingImage.id}`, {
        caption: editingImage.caption,
      });
      // Optimistic local update so the caption appears immediately without a full refetch.
      setOrderedImages((prev) => prev.map((i) => (i.id === editingImage.id ? { ...i, caption: editingImage.caption } : i)));
      toast.success("Légende mise à jour");
      setEditingImage(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'enregistrement");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // Client-side compression before upload (see utils/compressImage.js).
      const compressed = await compressImage(file);
      const fd = new FormData();
      fd.append("file", compressed);
      // Global axios interceptor injects the X-Admin-Token header.
      const res = await axios.post(`${API}/api/admin/gallery/upload-image`, fd);
      // Store the RELATIVE URL (starts with /api/...) so the image survives a
      // domain change (preview → auvergnetechracing.com). The browser resolves
      // relative URLs against the current origin at render time. External URLs
      // (http://...) pasted manually keep working as-is.
      setGalleryForm({ ...galleryForm, url: res.data.url });
      toast.success("Photo envoyée — clique sur « Ajouter » pour la publier");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Échec de l'upload");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <ImageIcon size={20} className="text-[#FF3B30]" />
          Galerie photos
        </h2>
        <span className="text-white/50 text-xs">{activeCount}/6 images actives</span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <StatCard icon={ImageIcon} label="Total images" value={galleryImages.length} color="text-white" accent="bg-white/[0.06]" />
        <StatCard icon={CheckCircle} label="Actives" value={activeCount} color="text-emerald-400" accent="bg-emerald-500/10" />
        <StatCard icon={EyeOff} label="Masquées" value={galleryImages.length - activeCount} color="text-white/60" accent="bg-white/[0.04]" />
      </div>

      {/* Add form */}
      <SectionCard title="Ajouter une image" icon={Plus}>
        <form onSubmit={handleAddImage} className="space-y-3">
          <div>
            <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Photo</label>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="gallery-upload-btn"
                className="inline-flex items-center justify-center gap-2 bg-[#FF3B30]/80 hover:bg-[#FF3B30] disabled:opacity-50 text-white font-bold px-4 py-2.5 rounded-md text-xs uppercase tracking-wider whitespace-nowrap transition-colors"
              >
                {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
                {uploading ? "Envoi…" : "Uploader une photo"}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/*,.heic,.heif"
                className="hidden"
                onChange={handleUpload}
                data-testid="gallery-upload-input"
              />
              <Input
                value={galleryForm.url}
                onChange={(e) => setGalleryForm({ ...galleryForm, url: e.target.value })}
                placeholder="… ou coller une URL https://…"
                data-testid="gallery-url-input"
                style={{ minHeight: 44 }}
                className="flex-1 bg-[#1C1C1E] border-white/[0.06] text-white text-base md:text-sm"
              />
            </div>
            <p className="text-white/30 text-[11px] mt-1.5">
              📱 Sur mobile, l'upload te propose <strong>appareil photo</strong> ou <strong>photothèque</strong>. Max 15 Mo par image.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-end">
            <div>
              <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Légende (optionnel)</label>
              <Input
                value={galleryForm.caption}
                onChange={(e) => setGalleryForm({ ...galleryForm, caption: e.target.value })}
                placeholder="Ex: Changement de pneumatiques été"
                className="bg-[#1C1C1E] border-white/[0.06] text-white h-9 text-sm"
              />
            </div>
            <button
              type="submit"
              data-testid="gallery-submit-btn"
              className="bg-[#FF3B30] hover:bg-[#D63025] text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors h-9 flex items-center gap-2"
            >
              <Plus size={14} /> Ajouter
            </button>
          </div>
        </form>
      </SectionCard>

      {/* Grid — drag & drop */}
      {orderedImages.length === 0 ? (
        <EmptyState icon={ImageIcon} title="Aucune image dans la galerie" />
      ) : (
        <>
          <div className="flex items-center justify-between text-xs">
            <p className="text-white/40">
              <GripVertical size={12} className="inline -mt-0.5" /> Glisse-dépose une vignette pour réordonner.
            </p>
            {reordering && <span className="text-[#FF3B30]">Enregistrement…</span>}
          </div>
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={orderedImages.map((i) => i.id)} strategy={rectSortingStrategy}>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {orderedImages.map((img) => (
                  <SortableGalleryTile
                    key={img.id}
                    img={img}
                    onToggle={handleToggleImage}
                    onDelete={handleDeleteImage}
                    onEdit={handleEditClick}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        </>
      )}

      {/* Edit caption modal */}
      {editingImage && (
        <div
          className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm overflow-y-auto"
          onClick={() => !savingEdit && setEditingImage(null)}
          data-testid="gallery-edit-modal"
        >
          <div className="min-h-full flex items-center justify-center p-4">
            <div
              className="relative bg-[#141414] border border-white/[0.08] rounded-xl w-full max-w-md p-5 my-8"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-bold flex items-center gap-2">
                  <Pencil size={16} className="text-[#FF3B30]" /> Modifier la légende
                </h3>
                <button
                  type="button"
                  onClick={() => !savingEdit && setEditingImage(null)}
                  data-testid="gallery-edit-close"
                  className="p-1.5 rounded-md hover:bg-white/[0.06] text-white/60 hover:text-white"
                  aria-label="Fermer"
                >
                  <X size={16} />
                </button>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-white/50 text-[11px] uppercase tracking-wider mb-1.5 block">Légende</label>
                  <input
                    type="text"
                    value={editingImage.caption}
                    onChange={(e) => setEditingImage({ ...editingImage, caption: e.target.value })}
                    placeholder="Ex : Changement pneumatiques été"
                    data-testid="gallery-edit-caption-input"
                    autoFocus
                    onKeyDown={(e) => e.key === "Enter" && handleSaveCaption()}
                    style={{ minHeight: 44 }}
                    className="w-full bg-[#1C1C1E] border border-white/[0.06] rounded-md px-3 py-2 text-white text-base md:text-sm focus:outline-none focus:border-white/20"
                  />
                  <p className="text-white/30 text-[11px] mt-1.5">
                    L'ordre se change désormais par glisser-déposer directement sur la grille.
                  </p>
                </div>
                <div className="flex items-center justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setEditingImage(null)}
                    disabled={savingEdit}
                    data-testid="gallery-edit-cancel"
                    className="bg-white/[0.06] hover:bg-white/[0.1] text-white/70 px-4 py-2 rounded-md text-sm font-semibold h-10 disabled:opacity-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="button"
                    onClick={handleSaveCaption}
                    disabled={savingEdit}
                    data-testid="gallery-edit-save"
                    className="bg-[#FF3B30] hover:bg-[#D63025] text-white px-4 py-2 rounded-md text-sm font-semibold h-10 flex items-center gap-2 disabled:opacity-50"
                  >
                    {savingEdit ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    {savingEdit ? "Enregistrement…" : "Enregistrer"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryTab;
